import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { api } from '../api/client.js'
import { useApi } from '../api/useApi.js'
import { pickI18n } from '../i18n'
import { timeAgo } from '../lib.js'
import { money } from '../lib/money.js'
import { ErrorState, Loading } from './states.jsx'

function Tile({ label, value, hint }) {
  return (
    <div className="flex flex-col gap-1 rounded-2xl bg-paper p-5">
      <span className="text-sm font-bold text-subtle">{label}</span>
      <span className="text-[28px] leading-tight font-black">{value}</span>
      {hint ? <span className="text-sm text-muted">{hint}</span> : null}
    </div>
  )
}

/** Where the money goes: raised, spent, meals funded and given, every donation and expense. */
export default function TransparencyPage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.resolvedLanguage
  const { data: s, error, loading } = useApi(() => api.donationSummary())
  if (loading) return <Loading />
  if (error) return <ErrorState />
  const fmt = (c) => money(c, s.currency, lang)
  return (
    <main className="mx-auto flex max-w-5xl flex-col gap-8 px-4 pt-10 pb-[72px] sm:px-8">
      <div className="flex flex-col gap-2">
        <h1 className="text-[clamp(32px,4vw,44px)] font-black tracking-[-1px]">{t('donate.transparency')}</h1>
        <p className="text-lg text-muted">{t('donate.transparencyLead')}</p>
      </div>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(210px,1fr))] gap-3">
        <Tile label={t('donate.raised')} value={fmt(s.raised_cents)} hint={t('donate.thisMonth', { amount: fmt(s.raised_this_month_cents) })} />
        <Tile label={t('donate.spent')} value={fmt(s.spent_cents)} hint={s.food_bought_grams ? t('donate.foodKg', { kg: (s.food_bought_grams / 1000).toFixed(1) }) : null} />
        <Tile label={t('donate.balance')} value={fmt(s.balance_cents)} />
        <Tile label={t('donate.mealsFunded')} value={s.meals_funded} hint={t('donate.mealsGiven', { count: s.meals_dispensed })} />
        <Tile label={t('donate.supporters')} value={s.donors} hint={t('donate.monthlyCount', { count: s.monthly_supporters })} />
      </div>
      {s.enabled ? (
        <Link to="/donate" className="self-start rounded-full bg-teal px-6 py-3 font-extrabold text-on-teal no-underline">{t('donate.title')}</Link>
      ) : null}
      <div className="grid gap-6 md:grid-cols-2">
        <section className="flex flex-col gap-3">
          <h2 className="text-2xl font-black">{t('donate.recent')}</h2>
          {s.recent.length ? (
            <ul className="m-0 flex list-none flex-col p-0">
              {s.recent.map((d, i) => (
                <li key={i} className="flex justify-between gap-3 border-b border-line py-3">
                  <span className="flex flex-col">
                    <span className="font-extrabold">{d.name ?? t('donate.anonymous')}</span>
                    <span className="text-sm text-subtle">
                      {d.feeder ? pickI18n(d.feeder.name, lang) : t('donate.generalFund')} · {timeAgo(d.paid_at, lang)}
                    </span>
                  </span>
                  <span className="text-right font-extrabold text-teal">
                    {fmt(d.amount_cents)}
                    {d.kind === 'monthly' ? <span className="block text-xs text-subtle">{t('donate.kind.monthly')}</span> : null}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted">{t('donate.noDonations')}</p>
          )}
        </section>
        <section className="flex flex-col gap-3">
          <h2 className="text-2xl font-black">{t('donate.expenses')}</h2>
          {s.expenses.length ? (
            <ul className="m-0 flex list-none flex-col p-0">
              {s.expenses.map((e, i) => (
                <li key={i} className="flex justify-between gap-3 border-b border-line py-3">
                  <span className="flex flex-col">
                    <span className="font-extrabold">{e.description}</span>
                    <span className="text-sm text-subtle">
                      {t(`donate.category.${e.category}`)} · {new Date(e.spent_on).toLocaleDateString(lang)}
                      {e.receipt_url ? (
                        <>
                          {' · '}
                          <a href={e.receipt_url} target="_blank" rel="noopener noreferrer" className="text-teal">{t('donate.receipt')}</a>
                        </>
                      ) : null}
                    </span>
                  </span>
                  <span className="font-extrabold">{fmt(e.amount_cents)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted">{t('donate.noExpenses')}</p>
          )}
        </section>
      </div>
    </main>
  )
}
